import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { SiteHeader, SiteFooter } from '../components/SiteChrome';
import type { MsgKey } from '../i18n/en';
// @ts-ignore: allow side-effect CSS import without type declarations
import '../App.css';

interface OrderStatus {
  status: string;
  amount?: number;
  platform?: string;
  created?: string;
  accountCheck?: string;
  accountCheckLong?: string;
  economyState?: string;
  economyStateLong?: string;
  amountOrdered?: number;
  percentDelivered?: number;
  coinsCustomerAccount?: number;
  wasAborted?: number;
  knownClub?: string;
  externalOrderID?: string;
  /**
   * Present only on orders where EA's transfer tax is the customer's.
   * amountPurchased is what they bought, amountNet what lands in their club
   * (and what was sent to FutTransfer). The API does the arithmetic so this
   * page, the admin panel and the welcome message can never disagree.
   */
  eaTaxPct?: number;
  amountPurchased?: number;
  amountNet?: number;
}

interface CooldownData {
  success: boolean;
  cooldownRemaining: number;
  message: string;
  isReady: boolean;
  hours?: number;
  minutes?: number;
}

/**
 * Behaviour of each known issue. The words live in i18n/en.ts under
 * err.<key>.t / .d / .s so they can be translated; this table only says how the
 * page should treat the issue.
 */
interface ErrorFlags {
  autoFix?: boolean;
  isDailyLimit?: boolean;
  contactSupport?: boolean;
  canRetry?: boolean;
}

const ERROR_FLAGS: Record<string, ErrorFlags> = {
  wrongPersona: { canRetry: true },
  captcha: { canRetry: true },
  wrongUserPass: { contactSupport: true },
  wrongBA: {},
  No2FA: {},
  noTM: { contactSupport: true },
  tlFull: {},
  receiverTLFull: { canRetry: true },
  FailedReceiverDeviceBan: { contactSupport: true },
  FailWebAppNotYetUnlocked: { contactSupport: true },
  FailWebAppCustomerLocked: { canRetry: true },
  calcErrorMaintenance: { autoFix: true },
  noPlayer: { autoFix: true },
  insufficientFunds: { autoFix: true },
  belowMinTransfer: { canRetry: true },
  notEnoughCoins: { canRetry: true },
  unassignedItemsPresent: { canRetry: true },
  console: { canRetry: true },
  loginFailed: { canRetry: true },
  LoginFailedDeviceBan: { contactSupport: true },
  FailedSessionExpiredCustomerLoggedIn: { canRetry: true },
  'FailedSessionExpiredCustomerLoggedIn?': { canRetry: true },
  FailLoggedInConsoleTo: { canRetry: true },
  FailedProxyConnectionError: { autoFix: true },
  FailedProxyPoolExhausted: { autoFix: true },
  FailedCouldNotStartTo: { autoFix: true },
  noClub: { contactSupport: true },
  wrongConsole: { contactSupport: true },
  PlayerLostTempban: { isDailyLimit: true, autoFix: true },
  customerTempban: { isDailyLimit: true, autoFix: true },
  tempbanCooldown: { isDailyLimit: true, autoFix: true },
  dailyReceiverLimit: { isDailyLimit: true, autoFix: true },
  senderTempban: { autoFix: true },
  noSuitableSender: { autoFix: true },
  transfersInProgress: { autoFix: true },
};

const platformLabel = (p?: string) => ({ PS: 'PlayStation', XB: 'Xbox', PC: 'PC' }[p ?? ''] || p || '');

const fmtTime = (ts: string, lang: string) => {
  try {
    const d = new Date(ts);
    return isNaN(d.getTime())
      ? ts
      : d.toLocaleString(lang, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
};

const DAILY_LIMIT_KEYS = new Set(['PlayerLostTempban', 'customerTempban', 'tempbanCooldown', 'dailyReceiverLimit']);

// Maps alternate API values (camelCase variants or display strings) → our ERROR_FLAGS key
const ERR_KEY_ALIASES: Record<string, string> = {
  'consoleLoggedIn':                       'console',
  'Console Logged In':                     'console',
  'loginFailed':                           'loginFailed',
  'Login Failed':                          'loginFailed',
  'wrongPersona':                          'wrongPersona',
  'Wrong Persona':                         'wrongPersona',
  'captcha':                               'captcha',
  'Captcha':                               'captcha',
  'wrongUserPass':                         'wrongUserPass',
  'Wrong User Pass':                       'wrongUserPass',
  'wrongBA':                               'wrongBA',
  'Wrong BA':                              'wrongBA',
  'No2FA':                                 'No2FA',
  'No 2FA Set Up':                         'No2FA',
  'no2FA':                                 'No2FA',
  'no2fa':                                 'No2FA',
  'No2fa':                                 'No2FA',
  'FailNo2FA':                             'No2FA',  // economyState variant
  '2FADisabled':                           'No2FA',
  'Fail No 2FA':                           'No2FA',
  'noTM':                                  'noTM',
  'No TM':                                 'noTM',
  'tlFull':                                'tlFull',
  'TL Full':                               'tlFull',
  'Transfer List Full':                    'tlFull',
  'receiverTLFull':                        'receiverTLFull',
  'FailedTLfullReceiver':                  'receiverTLFull',  // actual API key from docs
  'Receiver TL full':                      'receiverTLFull',
  'Receiver TL Full':                      'receiverTLFull',
  'ReceiverTLFull':                        'receiverTLFull',
  'receiver TL full':                      'receiverTLFull',
  'notEnoughCoins':                        'notEnoughCoins',
  'Not Enough Coins':                      'notEnoughCoins',
  'unassignedItemsPresent':                'unassignedItemsPresent',
  'Unassigned Items Present':              'unassignedItemsPresent',
  'LoginFailedDeviceBan':                  'LoginFailedDeviceBan',
  'Login Failed Device Ban':               'LoginFailedDeviceBan',
  'noClub':                                'noClub',
  'No Club':                               'noClub',
  'wrongConsole':                          'wrongConsole',
  'Wrong Console':                         'wrongConsole',
  'noSuitableSender':                      'noSuitableSender',
  'No Suitable Sender':                    'noSuitableSender',
  'senderTempban':                         'senderTempban',
  'Sender Tempban':                        'senderTempban',
  'transfersInProgress':                   'transfersInProgress',
  'Transfers In Progress':                 'transfersInProgress',
  'PlayerLostTempban':                     'PlayerLostTempban',
  'Player Lost Tempban':                   'PlayerLostTempban',
  'FailPlayerLostToTempban':               'PlayerLostTempban',
  'FailListTempBanCustomer':               'PlayerLostTempban',
  'customerTempban':                       'customerTempban',
  'Customer Tempban':                      'customerTempban',
  'tempbanCooldown':                       'tempbanCooldown',
  'Tempban Cooldown':                      'tempbanCooldown',
  'dailyReceiverLimit':                    'dailyReceiverLimit',
  'FailedProxyConnectionError':            'FailedProxyConnectionError',
  'FailedProxyPoolExhausted':              'FailedProxyPoolExhausted',
  'FailedCouldNotStartTo':                 'FailedCouldNotStartTo',
  'FailedSessionExpiredCustomerLoggedIn':  'FailedSessionExpiredCustomerLoggedIn',
  'FailedSessionExpiredCustomerLoggedIn?': 'FailedSessionExpiredCustomerLoggedIn?',
  'FailLoggedInConsoleTo':                 'FailLoggedInConsoleTo',
  // New errors from API docs
  'FailedWrongCredentialsTo':              'wrongUserPass',
  'FailedWrongBACodeTo':                   'wrongBA',
  'FailNoClubToCanBeEAErrorTryAgain':      'noClub',
  'FailedReceiverDeviceBan':               'FailedReceiverDeviceBan',
  'FailWebAppNotYetUnlocked':              'FailWebAppNotYetUnlocked',
  'FailWebAppCustomerLocked':              'FailWebAppCustomerLocked',
  'calcErrorMaintenance':                  'calcErrorMaintenance',
  'noPlayer':                              'noPlayer',
  'insufficientFunds':                     'insufficientFunds',
  'belowMinTransfer':                      'belowMinTransfer',
  'transferCycleComplete':                 'transfersInProgress',
  'customerHasPlayer':                     'transfersInProgress',
  'customerListedPlayer':                  'transfersInProgress',
};

const resolveErrKey = (val?: string): string | null => {
  if (!val) return null;
  if (ERROR_FLAGS[val]) return val;
  const alias = ERR_KEY_ALIASES[val];
  return alias ?? null;
};

// Supplier-side hiccups that clear within about a minute. The customer sees one
// calm "safety hold" message for all of them instead of a supply error.
const SAFETY_HOLD_KEYS = new Set(['insufficientFunds', 'noSuitableSender']);

/** The key whose i18n entries describe an issue (daily limits share one, so do safety holds). */
const errTextKey = (k: string) =>
  DAILY_LIMIT_KEYS.has(k) ? 'daily' : SAFETY_HOLD_KEYS.has(k) ? 'safetyHold' : k.replace(/\?$/, '');

const termsStorageKey = (name: string) => `tc_terms_${name}`;

interface Props { customerName: string; }

const TrackOrderPage: React.FC<Props> = ({ customerName }) => {
  const { t, rich, list, richList, has, lang } = useI18n();

  const [data, setData]           = useState<OrderStatus | null>(null);
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Screenshots
  const [ssVer, setSsVer]   = useState(Date.now());
  const [ss1Ok, setSs1Ok]   = useState(false);
  const [ss2Ok, setSs2Ok]   = useState(false);
  const prevStatusRef = useRef<string | null>(null);

  // Credential form
  const [user, setUser]           = useState('');
  const [pass, setPass]           = useState('');
  const [ba, setBa]               = useState('');
  const [ba2, setBa2]             = useState('');
  const [ba3, setBa3]             = useState('');
  const [showExtra, setShowExtra] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError]     = useState<string | null>(null);

  // Terms popup — shown while the order is still waiting for the customer's
  // details. Acceptance is remembered per order on this device, so a reload
  // doesn't make them read it again, but a different order always asks.
  const [termsOk, setTermsOk] = useState<boolean>(() => {
    try { return window.localStorage.getItem(termsStorageKey(customerName)) === '1'; } catch { return false; }
  });
  const [termsChecked, setTermsChecked]   = useState(false);
  const [termsDeclined, setTermsDeclined] = useState(false);

  // Resume
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeMsg, setResumeMsg] = useState<{ type: 'success' | 'warning' | 'danger'; text: string } | null>(null);

  // Cooldown
  const [cooldown, setCooldown]         = useState<CooldownData | null>(null);
  const [cooldownLoading, setCooldownLoading] = useState(false);

  // Wrong BA
  const [baNew, setBaNew]         = useState('');
  const [baLoading, setBaLoading] = useState(false);
  const [baResult, setBaResult]   = useState<'success' | 'error' | null>(null);

  // Wrong User/Pass
  const [newUser, setNewUser]       = useState('');
  const [newPass, setNewPass]       = useState('');
  const [passLoading, setPassLoading] = useState(false);
  const [passResult, setPassResult]   = useState<'success' | 'error' | null>(null);

  const apiBase = `/api/orders/${encodeURIComponent(customerName)}`;

  const fetchStatus = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await fetch(apiBase);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFetchError(d.error || t('msg.notFound'));
        return;
      }
      const d: OrderStatus = await res.json();
      setData(d);
      setFetchError(null);
      if (isManual || d.status !== prevStatusRef.current) {
        setSsVer(Date.now());
        setSs1Ok(false);
        setSs2Ok(false);
        prevStatusRef.current = d.status;
      }
    } catch {
      setFetchError(t('msg.netRetry'));
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Poll every 12s, but skip while the tab is hidden to save requests.
    const iv = setInterval(() => {
      if (!document.hidden) fetchStatus();
    }, 12000);
    // Refresh immediately when the user returns to the tab.
    const onVisible = () => { if (!document.hidden) fetchStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [customerName]); // fetchStatus is stable — eslint-disable-line

  const acceptTerms = () => {
    if (!termsChecked) return;
    setTermsOk(true);
    try { window.localStorage.setItem(termsStorageKey(customerName), '1'); } catch { /* storage blocked */ }
  };

  const reopenTerms = () => {
    setTermsOk(false);
    setTermsChecked(false);
    setTermsDeclined(false);
    try { window.localStorage.removeItem(termsStorageKey(customerName)); } catch { /* storage blocked */ }
  };

  const submittingRef = useRef(false);
  const handleSubmitCredentials = async () => {
    if (!termsOk) return;
    if (!user.trim() || !pass.trim() || !ba.trim()) return;
    // A ref, not state: two taps in the same tick both see submitLoading=false.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const body: Record<string, string> = { user: user.trim(), pass: pass.trim(), ba: ba.trim() };
      if (ba2.trim()) body.ba2 = ba2.trim();
      if (ba3.trim()) body.ba3 = ba3.trim();
      const res = await fetch(`${apiBase}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      // d.error is the server's own wording (English); the fallback is ours.
      if (!res.ok) setSubmitError(d.error || t('msg.submitFail'));
      else await fetchStatus(true);
    } catch {
      setSubmitError(t('msg.net'));
    } finally {
      submittingRef.current = false;
      setSubmitLoading(false);
    }
  };

  const handleResume = async () => {
    if (resumeLoading) return;
    setResumeLoading(true);
    setResumeMsg(null);
    try {
      const res = await fetch(`${apiBase}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json().catch(() => ({}));
      // resumeOrderAPI returns 200 {outcome:"resumed"} on success.
      if (res.ok) {
        setResumeMsg({ type: 'success', text: t('msg.resumed') });
        setTimeout(() => fetchStatus(), 2500);
      } else if (res.status === 429) {
        // 429 = short cooldown after a console login failure. Just wait and retry.
        setResumeMsg({ type: 'warning', text: t('msg.waitLogout') });
      } else if (res.status === 405) {
        // 405 = the order has a temp ban (NOT a daily limit). It clears on its own
        // and the order auto-resumes; surface the real reason if the API gave one.
        const reason = d?.error || d?.reason || d?.raw;
        setResumeMsg({
          type: 'warning',
          text: reason ? t('msg.cantResumeWhy', { reason }) : t('msg.cantResume'),
        });
        setTimeout(() => fetchStatus(), 2500);
      } else {
        setResumeMsg({ type: 'danger', text: d?.error || t('msg.resumeFail') });
      }
    } catch {
      setResumeMsg({ type: 'danger', text: t('msg.net') });
    } finally {
      setResumeLoading(false);
    }
  };

  // Retry for onboarding/login-stuck orders (console / EA-app logged in).
  // Hits correctCredentialsAPI with continue:1 (re-runs the process) instead of
  // resumeOrderAPI, which returns a temp-ban 405 for these states.
  const handleRetry = async () => {
    if (resumeLoading) return;
    setResumeLoading(true);
    setResumeMsg(null);
    try {
      const res = await fetch(`${apiBase}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setResumeMsg({ type: 'success', text: t('msg.retrying') });
        setTimeout(() => fetchStatus(), 2500);
      } else if (res.status === 429) {
        // Short cooldown after a console login failure — wait then retry.
        setResumeMsg({ type: 'warning', text: t('msg.waitLogout') });
      } else {
        setResumeMsg({ type: 'danger', text: d?.error || t('msg.retryFail') });
        setTimeout(() => fetchStatus(), 2500);
      }
    } catch {
      setResumeMsg({ type: 'danger', text: t('msg.net') });
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCooldown = async () => {
    if (cooldownLoading) return;
    setCooldownLoading(true);
    try {
      const res = await fetch(`${apiBase}/cooldown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) setCooldown(await res.json());
    } catch { /* ignore */ } finally {
      setCooldownLoading(false);
    }
  };

  const handleCorrectPass = async () => {
    if ((!newUser.trim() && !newPass.trim()) || passLoading) return;
    setPassLoading(true);
    setPassResult(null);
    try {
      const res = await fetch(`/api/orders/${encodeURIComponent(customerName)}/correctBA`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: newUser.trim() || undefined, pass: newPass.trim() || undefined }),
      });
      setPassResult(res.ok ? 'success' : 'error');
      if (res.ok) { setNewUser(''); setNewPass(''); setTimeout(fetchStatus, 3000); }
    } catch { setPassResult('error'); }
    finally { setPassLoading(false); }
  };

  const handleCorrectBA = async () => {
    if (!baNew.trim() || baLoading) return;
    setBaLoading(true);
    setBaResult(null);
    try {
      const res = await fetch(`${apiBase}/correctBA`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ba: baNew.trim() }),
      });
      if (res.ok) {
        setBaResult('success');
        setBaNew('');
        setTimeout(() => fetchStatus(), 3000);
      } else {
        setBaResult('error');
      }
    } catch {
      setBaResult('error');
    } finally {
      setBaLoading(false);
    }
  };

  // ── Derived state ────────────────────────────────────────────────
  const isPending    = data?.status === 'pending';
  const isCancelled  = data?.status === 'cancelled';
  const isFinished   = data?.status === 'finished';
  const isInterrupted = data?.status === 'interrupted';
  const isTempbanStatus = data?.status === 'tempbanCooldown';

  const delivered  = data?.amount ?? 0;
  const ordered    = data?.amountOrdered ?? data?.amount ?? 0;
  const remaining  = ordered > 0 ? Math.max(ordered - delivered, 0) : 0;
  const pct        = data?.percentDelivered ?? 0;
  const showProgress = !isPending && data && ordered > 0;

  // EA tax is the customer's on this order. For such orders FutTransfer's
  // "ordered" figure is already the net, so progress below is in coins that
  // actually land — and is labelled that way.
  const taxed = data?.eaTaxPct !== undefined && data?.amountPurchased !== undefined && data?.amountNet !== undefined;
  const taxVars = taxed
    ? {
        pct: data!.eaTaxPct as number,
        gross: (data!.amountPurchased as number).toLocaleString(),
        net: (data!.amountNet as number).toLocaleString(),
      }
    : null;

  // Collect error keys from API response — try accountCheck then accountCheckLong
  const activeErrKeys = new Set<string>();
  const acKey = resolveErrKey(data?.accountCheck) ?? resolveErrKey(data?.accountCheckLong);
  if (acKey) activeErrKeys.add(acKey);
  const esKey = resolveErrKey(data?.economyState) ?? resolveErrKey(data?.economyStateLong);
  if (esKey) activeErrKeys.add(esKey);
  if (isTempbanStatus) activeErrKeys.add('tempbanCooldown');

  const isDailyLimit = [...activeErrKeys].some(k => DAILY_LIMIT_KEYS.has(k));
  // These errors can arrive via accountCheck OR economyState (e.g. FailNo2FA)
  const isWrongBA        = acKey === 'wrongBA'       || esKey === 'wrongBA';
  const isNo2FA          = acKey === 'No2FA'         || esKey === 'No2FA';
  const isWrongUserPass  = acKey === 'wrongUserPass' || esKey === 'wrongUserPass';

  const nonDailyErrors = [...activeErrKeys].filter(k => !DAILY_LIMIT_KEYS.has(k));
  const actionableErrors = nonDailyErrors.filter(k => !ERROR_FLAGS[k]?.autoFix && k !== 'wrongBA' && k !== 'No2FA' && k !== 'wrongUserPass');
  const autoFixErrors    = nonDailyErrors.filter(k => ERROR_FLAGS[k]?.autoFix);

  const errTitle = (k: string) => t(`err.${errTextKey(k)}.t` as MsgKey);
  const errDesc  = (k: string) => (has(`err.${errTextKey(k)}.d`) ? t(`err.${errTextKey(k)}.d` as MsgKey) : '');
  const errSteps = (k: string) => (has(`err.${errTextKey(k)}.s`) ? list(`err.${errTextKey(k)}.s` as MsgKey) : []);

  // Hero config
  const getHero = () => {
    if (loading) return { color: 'muted', icon: 'fa-circle-notch fa-spin', label: t('hero.loading'), badge: '' };
    if (fetchError) return { color: 'danger', icon: 'fa-exclamation-circle', label: t('hero.notFound'), badge: '' };
    if (!data) return { color: 'muted', icon: 'fa-question-circle', label: t('hero.unknown'), badge: '' };
    if (isCancelled) return { color: 'danger', icon: 'fa-ban', label: t('hero.cancelled'), badge: t('badge.cancelled') };
    if (isPending) return { color: 'info', icon: 'fa-clock', label: t('hero.awaiting'), badge: t('badge.action') };
    if (isFinished) return { color: 'success', icon: 'fa-check-circle', label: t('hero.delivered'), badge: t('badge.complete') };
    if (isDailyLimit) return { color: 'warning', icon: 'fa-moon', label: t('hero.dailyLimit'), badge: t('badge.autoResume') };
    if (isNo2FA)         return { color: 'danger', icon: 'fa-shield-alt', label: t('hero.no2fa'), badge: t('badge.action') };
    if (isWrongBA)       return { color: 'danger', icon: 'fa-key', label: t('hero.newBA'), badge: t('badge.action') };
    if (isWrongUserPass) return { color: 'danger', icon: 'fa-lock', label: t('hero.wrongPass'), badge: t('badge.action') };
    if (actionableErrors.length > 0) {
      const k = actionableErrors[0];
      if (ERROR_FLAGS[k]?.contactSupport) return { color: 'danger', icon: 'fa-life-ring', label: errTitle(k), badge: t('badge.support') };
      return { color: 'warning', icon: 'fa-exclamation-triangle', label: errTitle(k), badge: t('badge.action') };
    }
    if (autoFixErrors.length > 0 && !isInterrupted)
      return { color: 'info', icon: 'fa-circle-notch fa-spin', label: t('hero.processing'), badge: t('badge.retry') };
    if (isInterrupted) return { color: 'warning', icon: 'fa-pause-circle', label: t('hero.paused'), badge: t('badge.action') };
    return { color: 'active', icon: 'fa-circle-notch fa-spin', label: t('hero.delivering'), badge: t('badge.progress') };
  };

  const hero = getHero();

  const displayStatus = data
    ? (has(`status.${data.status}`)
        ? t(`status.${data.status}` as MsgKey)
        : data.status.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim())
    : null;

  const showTerms = !loading && !fetchError && isPending && !isCancelled && !termsOk;

  // Lock page scroll behind the popup so it can't be scrolled around.
  useEffect(() => {
    if (!showTerms) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showTerms]);

  const termsItems: React.ReactNode[] = [
    rich('terms.p1'),
    rich('terms.p2'),
    ...(taxVars ? [rich('terms.p3', taxVars)] : []),
    rich('terms.p4'),
  ];

  return (
    <div className="tc-page">
      {/* ── Terms popup ──────────────────────────────────────── */}
      {showTerms && (
        <div className="tc-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="tc-terms-title">
          <div className="tc-modal">
            {!termsDeclined ? (
              <>
                <h2 className="tc-modal-title" id="tc-terms-title">
                  <i className="fas fa-clipboard-check"></i>
                  {t('terms.title')}
                </h2>
                <p className="tc-modal-intro">{t('terms.intro')}</p>
                <ol className="tc-terms-list">
                  {termsItems.map((item, i) => (
                    <li key={i} className="tc-terms-item">
                      <span className="tc-terms-num">{i + 1}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
                <label className="tc-terms-check">
                  <input
                    type="checkbox"
                    checked={termsChecked}
                    onChange={e => setTermsChecked(e.target.checked)}
                  />
                  <span>{t('terms.agree')}</span>
                </label>
                <div className="tc-modal-actions">
                  <button
                    className="tc-btn tc-btn-primary"
                    onClick={acceptTerms}
                    disabled={!termsChecked}
                  >
                    <i className="fas fa-check"></i>
                    {t('terms.accept')}
                  </button>
                  <button className="tc-btn tc-btn-ghost" onClick={() => setTermsDeclined(true)}>
                    {t('terms.decline')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="tc-modal-title" id="tc-terms-title">
                  <i className="fas fa-life-ring"></i>
                  {t('terms.declinedTitle')}
                </h2>
                <p className="tc-modal-intro">{t('terms.declinedBody')}</p>
                <div className="tc-modal-actions">
                  <button className="tc-btn tc-btn-ghost" onClick={() => setTermsDeclined(false)}>
                    <i className="fas fa-arrow-left"></i>
                    {t('terms.back')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <SiteHeader badge="nav.tracker" />

      {/* ── Status Hero ──────────────────────────────────────── */}
      <div className={`tr-hero tr-hero-${hero.color}`}>
        <div className="tr-hero-inner">
          <div className="tr-hero-icon-wrap">
            <i className={`fas ${hero.icon}`}></i>
          </div>
          <div className="tr-hero-text">
            {hero.badge && <span className="tr-hero-badge">{hero.badge}</span>}
            <h1 className="tr-hero-label">{hero.label}</h1>
            <p className="tr-hero-order">
              {t('common.order', { id: customerName })}
              {data?.platform && (
                <span className="tc-platform-badge">{platformLabel(data.platform)}</span>
              )}
            </p>
            {/* Same line, same place as the Robux tracker's "reaches you". */}
            {taxVars && !isCancelled && (
              <p className="tr-hero-pct">{t('tax.hero', taxVars)}</p>
            )}
            {showProgress && pct > 0 && (
              <p className="tr-hero-pct">
                {t('hero.pct', { pct, delivered: delivered.toLocaleString(), ordered: ordered.toLocaleString() })}
              </p>
            )}
          </div>
          <button
            className="tr-hero-refresh"
            onClick={() => fetchStatus(true)}
            disabled={refreshing || loading}
            title={t('hero.refresh')}
          >
            <i className={`fas fa-sync-alt${refreshing ? ' fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* ── Content Card ─────────────────────────────────────── */}
      <div className="tr-content">

        {loading && (
          <div className="tc-alert tc-alert-info">
            <i className="fas fa-circle-notch fa-spin"></i>
            {t('common.loading')}
          </div>
        )}

        {fetchError && (
          <div className="tc-alert tc-alert-danger">
            <i className="fas fa-exclamation-circle"></i>
            {fetchError}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            CANCELLED
            ══════════════════════════════════════════════════════ */}
        {!loading && !fetchError && isCancelled && (
          <div className="tc-alert tc-alert-danger" style={{ alignItems: 'flex-start' }}>
            <i className="fas fa-ban" style={{ marginTop: 2 }}></i>
            <div>
              <strong>{t('msg.cancelledTitle')}</strong><br />
              <span style={{ fontWeight: 400 }}>{t('msg.cancelledBody')}</span>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            PENDING — customer hasn't submitted credentials yet
            ══════════════════════════════════════════════════════ */}
        {!loading && !fetchError && isPending && !isCancelled && (
          <>
            <div className="tc-banner">
              <i className="fas fa-info-circle tc-banner-icon"></i>
              <div>
                {rich('pending.banner', {
                  amount: (taxed ? data!.amountPurchased : data!.amount)?.toLocaleString() ?? '',
                  platform: platformLabel(data!.platform),
                })}
              </div>
            </div>

            {taxVars && (
              <div className="tc-tax-note">
                <i className="fas fa-percent"></i>
                <div>
                  <span className="tc-tax-note-title">{t('tax.noteTitle', taxVars)}</span>
                  {rich('tax.noteBody', taxVars)}
                </div>
              </div>
            )}

            <div className="tc-action-block tc-action-submit">
              <div className="tc-action-block-header">
                <i className="fas fa-gamepad"></i>
                <span>{t('pending.header')}</span>
              </div>
              <div className="tc-alert tc-alert-warning" style={{ marginTop: 0, marginBottom: 0 }}>
                <i className="fas fa-exclamation-triangle"></i>
                <span>{rich('pending.logout')}</span>
              </div>

              {submitError && (
                <div className="tc-alert tc-alert-danger">
                  <i className="fas fa-exclamation-circle"></i>
                  {submitError}
                </div>
              )}

              {!termsOk && (
                <div className="tc-alert tc-alert-warning">
                  <i className="fas fa-lock"></i>
                  {t('pending.locked')}
                </div>
              )}

              <div className="tc-form">
                <div className="tc-field">
                  <label className="tc-label">{t('pending.email')}</label>
                  <input
                    className="tc-input"
                    type="email"
                    placeholder={t('pending.emailPh')}
                    value={user}
                    onChange={e => setUser(e.target.value)}
                    disabled={submitLoading || !termsOk}
                  />
                </div>
                <div className="tc-field">
                  <label className="tc-label">{t('pending.password')}</label>
                  <input
                    className="tc-input"
                    type="password"
                    placeholder={t('pending.passwordPh')}
                    value={pass}
                    onChange={e => setPass(e.target.value)}
                    disabled={submitLoading || !termsOk}
                  />
                </div>
                <div className="tc-field">
                  <label className="tc-label">
                    {t('pending.ba1')}
                    <span className="tc-label-hint">{t('pending.ba1Hint')}</span>
                  </label>
                  <input
                    className="tc-input"
                    type="text"
                    placeholder={t('pending.ba1Ph')}
                    value={ba}
                    onChange={e => setBa(e.target.value)}
                    disabled={submitLoading || !termsOk}
                  />
                </div>

                {showExtra && (
                  <>
                    <div className="tc-field">
                      <label className="tc-label">
                        {t('pending.ba2')}
                        <span className="tc-label-hint">{t('pending.optional')}</span>
                      </label>
                      <input
                        className="tc-input"
                        type="text"
                        placeholder="e.g. 87654321"
                        value={ba2}
                        onChange={e => setBa2(e.target.value)}
                        disabled={submitLoading || !termsOk}
                      />
                    </div>
                    <div className="tc-field">
                      <label className="tc-label">
                        {t('pending.ba3')}
                        <span className="tc-label-hint">{t('pending.optional')}</span>
                      </label>
                      <input
                        className="tc-input"
                        type="text"
                        placeholder="e.g. 11223344"
                        value={ba3}
                        onChange={e => setBa3(e.target.value)}
                        disabled={submitLoading || !termsOk}
                      />
                    </div>
                  </>
                )}

                <button
                  type="button"
                  className="tc-link-btn"
                  onClick={() => setShowExtra(v => !v)}
                >
                  {showExtra ? t('pending.hideExtra') : t('pending.showExtra')}
                </button>

                <button
                  className="tc-btn tc-btn-primary tc-btn-block"
                  onClick={handleSubmitCredentials}
                  disabled={submitLoading || !termsOk || !user.trim() || !pass.trim() || !ba.trim()}
                >
                  <i className={`fas ${submitLoading ? 'fa-circle-notch fa-spin' : 'fa-rocket'}`}></i>
                  {submitLoading ? t('pending.starting') : t('pending.start')}
                </button>

                {termsOk && (
                  <button type="button" className="tc-link-btn" onClick={reopenTerms}>
                    {t('pending.reopenTerms')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════
            ACTIVE — order is running
            ══════════════════════════════════════════════════════ */}
        {!loading && !fetchError && !isPending && !isCancelled && data && (
          <>
            {/* Finished */}
            {isFinished && (
              <div className="tc-alert tc-alert-success">
                <i className="fas fa-check-circle"></i>
                <div>
                  <strong>{t('active.delivered')}</strong><br />
                  <span style={{ fontWeight: 400 }}>{t('active.thanks')}</span>
                  {taxVars && (
                    <>
                      <br />
                      <span style={{ fontWeight: 400 }}>{rich('tax.finished', taxVars)}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Stay logged out banner */}
            {!isFinished && (
              <div className="tc-banner">
                <i className="fas fa-shield-alt tc-banner-icon"></i>
                <div>
                  {rich('active.stayOut')}
                  <span className="tc-banner-meta">{t('active.meta')}</span>
                </div>
              </div>
            )}

            {/* ── Daily Transfer Limit ─────────────────────── */}
            {isDailyLimit && (
              <div className="tr-daily-limit">
                <div className="tr-daily-limit-header">
                  <i className="fas fa-moon"></i>
                  <span>{t('active.dailyHeader')}</span>
                </div>
                <p className="tr-daily-limit-body">{t('active.dailyBody')}</p>
              </div>
            )}

            {/* ── Actionable Errors ────────────────────────── */}
            {actionableErrors.map(key => {
              const flags = ERROR_FLAGS[key];
              if (!flags) return null;
              const desc = errDesc(key);
              const steps = errSteps(key);
              return (
                <div key={key} className={`tr-error-block${flags.contactSupport ? ' tr-error-support' : ''}`}>
                  <div className="tr-error-header">
                    <i className={`fas ${flags.contactSupport ? 'fa-life-ring' : 'fa-exclamation-triangle'}`}></i>
                    <span>{errTitle(key)}</span>
                  </div>
                  {desc && <p className="tr-error-desc">{desc}</p>}
                  {steps.length > 0 && (
                    <ol className="tr-error-steps">
                      {steps.map((step, i) => (
                        <li key={i} className="tr-error-step">{step}</li>
                      ))}
                    </ol>
                  )}
                  {flags.canRetry && (
                    <>
                      <button
                        className="tc-btn tc-btn-primary"
                        onClick={handleRetry}
                        disabled={resumeLoading}
                        style={{ marginTop: 8 }}
                      >
                        <i className={`fas ${resumeLoading ? 'fa-circle-notch fa-spin' : 'fa-redo'}`}></i>
                        {resumeLoading ? t('active.retrying') : t('active.retryBtn')}
                      </button>
                      {resumeMsg && (
                        <div className={`tc-alert tc-alert-${resumeMsg.type}`} style={{ marginTop: 8, marginBottom: 0 }}>
                          <i className={`fas ${resumeMsg.type === 'success' ? 'fa-check-circle' : resumeMsg.type === 'warning' ? 'fa-clock' : 'fa-exclamation-circle'}`}></i>
                          {resumeMsg.text}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Auto-fix technical errors (subtle) */}
            {autoFixErrors.length > 0 && actionableErrors.length === 0 && !isDailyLimit && (
              <div className="tc-alert tc-alert-info">
                <i className="fas fa-circle-notch fa-spin"></i>
                <div>
                  <strong>{errTitle(autoFixErrors[0])}</strong><br />
                  <span style={{ fontWeight: 400 }}>{errDesc(autoFixErrors[0])}</span>
                </div>
              </div>
            )}

            {/* ── Progress Metrics ─────────────────────────── */}
            {showProgress && (
              <div className="tc-metrics">
                <div className="tc-metric-card">
                  <span className="tc-metric-label">
                    <i className="fas fa-shopping-cart"></i> {taxed ? t('metric.willReceive') : t('metric.ordered')}
                  </span>
                  <span className="tc-metric-value">{ordered.toLocaleString()}K</span>
                </div>
                <div className="tc-metric-card tc-metric-delivered">
                  <span className="tc-metric-label"><i className="fas fa-check"></i> {t('metric.delivered')}</span>
                  <span className="tc-metric-value">{delivered.toLocaleString()}K</span>
                </div>
                <div className="tc-metric-card">
                  <span className="tc-metric-label"><i className="fas fa-hourglass-half"></i> {t('metric.remaining')}</span>
                  <span className="tc-metric-value">{remaining.toLocaleString()}K</span>
                </div>
              </div>
            )}

            {/* ── Progress Bar ─────────────────────────────── */}
            {showProgress && (
              <div className="tc-progress-block">
                <div className="tc-progress-header">
                  <span>{t('progress.title')}</span>
                  <span>{pct}%</span>
                </div>
                <div className="tc-progress-track">
                  <div className="tc-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="tc-progress-header">
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    {pct === 0 ? t('progress.starting') : pct < 100 ? t('progress.inProgress') : t('progress.complete')}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    {delivered.toLocaleString()}K / {ordered.toLocaleString()}K
                  </span>
                </div>
              </div>
            )}

            {/* ── Wrong BA / No 2FA Form ───────────────────── */}
            {(isWrongBA || isNo2FA) && (
              <div className="tc-action-block tc-action-ba">
                <div className="tc-action-block-header">
                  <i className={`fas ${isNo2FA ? 'fa-shield-alt' : 'fa-key'}`}></i>
                  <span>{isNo2FA ? t('ba.titleNo2fa') : t('ba.titleNew')}</span>
                </div>
                <p className="tc-action-block-desc">
                  {isNo2FA ? t('ba.descNo2fa') : t('ba.descNew')}
                </p>
                <ol className="tr-error-steps">
                  {richList(isNo2FA ? 'ba.no2faSteps' : 'ba.newSteps').map((step, i) => (
                    <li key={i} className="tr-error-step">{step}</li>
                  ))}
                </ol>
                <div className="tc-ba-form">
                  <input
                    className="tc-ba-input"
                    type="text"
                    placeholder={t('ba.placeholder')}
                    value={baNew}
                    onChange={e => setBaNew(e.target.value)}
                    disabled={baLoading}
                    maxLength={20}
                  />
                  <button
                    className="tc-btn tc-btn-primary"
                    onClick={handleCorrectBA}
                    disabled={baLoading || !baNew.trim()}
                  >
                    <i className={`fas ${baLoading ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'}`}></i>
                    {t('common.submit')}
                  </button>
                </div>
                {baResult === 'success' && (
                  <div className="tc-alert tc-alert-success">
                    <i className="fas fa-check-circle"></i>
                    {t('ba.ok')}
                  </div>
                )}
                {baResult === 'error' && (
                  <div className="tc-alert tc-alert-danger">
                    <i className="fas fa-exclamation-circle"></i>
                    {t('ba.fail')}
                  </div>
                )}
              </div>
            )}

            {/* ── Wrong User/Pass Form ─────────────────────── */}
            {isWrongUserPass && (
              <div className="tc-action-block tc-action-ba">
                <div className="tc-action-block-header">
                  <i className="fas fa-lock"></i>
                  <span>{t('cred.title')}</span>
                </div>
                <p className="tc-action-block-desc">{t('cred.desc')}</p>
                <ol className="tr-error-steps">
                  {richList('cred.steps').map((step, i) => (
                    <li key={i} className="tr-error-step">{step}</li>
                  ))}
                </ol>
                <div className="tc-ba-form" style={{ flexDirection: 'column', gap: 8 }}>
                  <input
                    className="tc-ba-input"
                    type="email"
                    placeholder={t('cred.emailPh')}
                    value={newUser}
                    onChange={e => setNewUser(e.target.value)}
                    disabled={passLoading}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="tc-ba-input"
                      type="password"
                      placeholder={t('cred.passPh')}
                      value={newPass}
                      onChange={e => setNewPass(e.target.value)}
                      disabled={passLoading}
                    />
                    <button
                      className="tc-btn tc-btn-primary"
                      onClick={handleCorrectPass}
                      disabled={passLoading || (!newUser.trim() && !newPass.trim())}
                      style={{ flexShrink: 0 }}
                    >
                      <i className={`fas ${passLoading ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'}`}></i>
                      {t('common.submit')}
                    </button>
                  </div>
                </div>
                {passResult === 'success' && (
                  <div className="tc-alert tc-alert-success" style={{ marginTop: 8 }}>
                    <i className="fas fa-check-circle"></i>
                    {t('cred.ok')}
                  </div>
                )}
                {passResult === 'error' && (
                  <div className="tc-alert tc-alert-danger" style={{ marginTop: 8 }}>
                    <i className="fas fa-exclamation-circle"></i>
                    {t('cred.fail')}
                  </div>
                )}
              </div>
            )}

            {/* ── Resume Order ──────────────────────────────── */}
            {isInterrupted && !isDailyLimit && !isNo2FA && !isWrongBA && !isWrongUserPass && (
              <div className="tc-action-block tc-action-resume">
                <div className="tc-action-block-header">
                  <i className="fas fa-pause-circle"></i>
                  <span>{t('resume.title')}</span>
                </div>
                <p className="tc-action-block-desc">{t('resume.desc')}</p>
                <button
                  className="tc-btn tc-btn-primary"
                  onClick={handleResume}
                  disabled={resumeLoading}
                >
                  <i className={`fas ${resumeLoading ? 'fa-circle-notch fa-spin' : 'fa-play'}`}></i>
                  {resumeLoading ? t('resume.busy') : t('resume.btn')}
                </button>
                {resumeMsg && (
                  <div className={`tc-alert tc-alert-${resumeMsg.type}`}>
                    <i className={`fas ${resumeMsg.type === 'success' ? 'fa-check-circle' : resumeMsg.type === 'warning' ? 'fa-clock' : 'fa-exclamation-circle'}`}></i>
                    {resumeMsg.text}
                  </div>
                )}
              </div>
            )}

            {/* ── Cooldown Check ────────────────────────────── */}
            {(isDailyLimit || isTempbanStatus) && (
              <div className="tc-action-block tc-action-cooldown">
                <div className="tc-action-block-header">
                  <i className="fas fa-hourglass-start"></i>
                  <span>{t('cool.title')}</span>
                  <button
                    className="tc-btn-sm"
                    onClick={handleCooldown}
                    disabled={cooldownLoading}
                  >
                    <i className={`fas ${cooldownLoading ? 'fa-circle-notch fa-spin' : 'fa-clock'}`}></i>
                    {cooldownLoading ? t('cool.busy') : t('cool.btn')}
                  </button>
                </div>
                <p className="tc-action-block-desc">{t('cool.desc')}</p>
                {cooldown && (
                  <div className={`tc-cooldown-result${cooldown.isReady ? ' tc-cooldown-ready' : ''}`}>
                    <i className={`fas ${cooldown.isReady ? 'fa-check-circle' : 'fa-clock'}`}></i>
                    {cooldown.isReady
                      ? t('cool.ready')
                      : cooldown.hours !== undefined
                        ? t('cool.hm', { h: cooldown.hours, m: cooldown.minutes ?? 0 })
                        : t('cool.min', { m: Math.ceil(cooldown.cooldownRemaining / 60) })}
                  </div>
                )}
              </div>
            )}

            {/* ── Screenshots ───────────────────────────────── */}
            <div className="tr-screenshots">
              <div className="tc-screenshot-block">
                <div className="tc-screenshot-label">
                  <i className="fas fa-id-badge"></i>
                  {t('shot.before')}
                </div>
                <div className="tc-screenshot-body">
                  <img
                    src={`${apiBase}/screenshot?mode=1&v=${ssVer}`}
                    alt={t('shot.before')}
                    className="tc-screenshot-img"
                    onLoad={() => setSs1Ok(true)}
                    onError={() => setSs1Ok(false)}
                    style={{ display: ss1Ok ? 'block' : 'none' }}
                  />
                  {!ss1Ok && <span className="tc-screenshot-empty">{t('shot.none')}</span>}
                </div>
              </div>

              <div className="tc-screenshot-block">
                <div className="tc-screenshot-label">
                  <i className="fas fa-camera"></i>
                  {t('shot.latest')}
                </div>
                <div className="tc-screenshot-body">
                  <img
                    src={`${apiBase}/screenshot?mode=2&v=${ssVer}`}
                    alt={t('shot.latest')}
                    className="tc-screenshot-img"
                    onLoad={() => setSs2Ok(true)}
                    onError={() => setSs2Ok(false)}
                    style={{ display: ss2Ok ? 'block' : 'none' }}
                  />
                  {!ss2Ok && <span className="tc-screenshot-empty">{t('shot.none')}</span>}
                </div>
              </div>
            </div>

            {/* ── Status Details ────────────────────────────── */}
            <div className="tc-divider" />
            <div className="tc-status-row">
              <div className="tc-status-item">
                <span className="tc-status-label">{t('row.status')}</span>
                <span className="tc-status-value">
                  <span className={`tc-dot ${
                    isFinished ? 'tc-dot-success' :
                    isDailyLimit || isInterrupted ? 'tc-dot-warning' :
                    ['ready', 'entered', 'waitingForAssignment', 'active'].includes(data.status) ? 'tc-dot-info' :
                    'tc-dot-muted'
                  }`} />
                  {displayStatus}
                </span>
              </div>
              {data.accountCheck && (
                <div className="tc-status-item">
                  <span className="tc-status-label">{t('row.accountCheck')}</span>
                  <span className="tc-status-value">
                    {data.accountCheckLong || data.accountCheck}
                  </span>
                </div>
              )}
              {data.economyState && (
                <div className="tc-status-item">
                  <span className="tc-status-label">{t('row.economy')}</span>
                  <span className="tc-status-value">
                    {data.economyStateLong || data.economyState}
                  </span>
                </div>
              )}
              {data.coinsCustomerAccount !== undefined && (
                <div className="tc-status-item">
                  <span className="tc-status-label">{t('row.balance')}</span>
                  <span className="tc-status-value">
                    {t('row.coins', { n: data.coinsCustomerAccount.toLocaleString() })}
                  </span>
                </div>
              )}
              {data.created && (
                <div className="tc-status-item">
                  <span className="tc-status-label">{t('row.created')}</span>
                  <span className="tc-status-value">{fmtTime(data.created, lang)}</span>
                </div>
              )}
            </div>
          </>
        )}

      </div>

      <SiteFooter />
    </div>
  );
};

export default TrackOrderPage;
